"use client";

import { useState, useEffect, useCallback, ChangeEvent } from "react";
import { PageLayout } from "@/components/ui/PageLayout";
import { useSession } from "@/utils/auth";
import { ApiKeyEntry, ApiKeyListResponse } from "@/utils/types";

export default function DeveloperPage() {
  const { data: session, status } = useSession();

  const [apiKeys, setApiKeys] = useState<ApiKeyEntry[]>([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const fetchKeys = useCallback(async () => {
    const res = await fetch(`/api/developer/apikey/list`);
    if (res.ok) {
      const data = (await res.json()) as ApiKeyListResponse;
      setApiKeys(data.keys ?? []);
    }
  }, []);

  useEffect(() => {
    if (session?.user) {
      void fetchKeys();
    }
  }, [session, fetchKeys]);

  if (status === "loading") {
    return (
      <PageLayout>
        <div className="max-w-[680px] mx-auto w-full">
          <p style={{ fontSize: 11, color: "#4a4a4a" }}>Loading session...</p>
        </div>
      </PageLayout>
    );
  }
  if (status === "unauthenticated") {
    return (
      <PageLayout>
        <div className="max-w-[680px] mx-auto w-full">
          <p style={{ fontSize: 11, color: "#4a4a4a" }}>You must be logged in to view Developer Settings.</p>
        </div>
      </PageLayout>
    );
  }

  const generateApiKey = async () => {
    setIsCreating(true);
    const res = await fetch(`/api/developer/apikey/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newKeyName }),
    });
    setIsCreating(false);
    if (res.ok) {
      const data = (await res.json()) as { success: boolean; key: string };
      if (!data.key) throw new Error("Failed to generate API key");
      setGeneratedKey(data.key);
      await fetchKeys();
    }
  };

  const copyToClipboard = async (key: string) => {
    await navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => { setCopied(false); }, 2000);
  };

  return (
    <PageLayout>
      <div className="max-w-[680px] mx-auto w-full">
        {/* Page title */}
        <h1
          className="font-serif italic leading-tight mb-1.5"
          style={{ fontSize: 36, color: "#f0ece4", letterSpacing: "-0.02em" }}
        >
          Developer
        </h1>
        <p style={{ fontSize: 11, color: "#888", lineHeight: 1.6, marginBottom: 40 }}>
          Manage your API keys and developer settings
        </p>

        <div className="flex flex-col" style={{ gap: 10 }}>
          {/* Create API Key */}
          <div
            style={{
              background: "#0e0e0e",
              border: "1px solid #1c1c1c",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <div
              className="flex items-start justify-between"
              style={{ padding: "18px 20px", borderBottom: "1px solid #1c1c1c", gap: 12 }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", marginBottom: 4 }}>
                  Create API Key
                </div>
                <div style={{ fontSize: 10, color: "#4a4a4a", letterSpacing: "0.02em", lineHeight: 1.5 }}>
                  Generate a new API key to authenticate requests to the Hoops API
                </div>
              </div>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <div className="flex flex-col sm:flex-row" style={{ gap: 8 }}>
                <input
                  type="text"
                  placeholder="Key name (e.g. my-app)"
                  value={newKeyName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => { setNewKeyName(e.target.value); }}
                  className="flex-1"
                  style={{
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: 8,
                    fontFamily: "'Geist Mono', monospace",
                    fontSize: 11,
                    color: "#d4cfc6",
                    padding: "9px 14px",
                    outline: "none",
                    letterSpacing: "0.04em",
                  }}
                />
                <button
                  onClick={() => void generateApiKey()}
                  disabled={!newKeyName.trim() || isCreating || !session?.user}
                  className="shrink-0 flex items-center gap-2"
                  style={{
                    fontFamily: "'Geist Mono', monospace",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    background: "#3ecf7e",
                    color: "#051a0e",
                    border: "none",
                    borderRadius: 8,
                    padding: "9px 18px",
                    cursor: !newKeyName.trim() || isCreating ? "not-allowed" : "pointer",
                    opacity: !newKeyName.trim() || isCreating ? 0.5 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M7.5 1.5L10.5 4.5L5.5 9.5L1 11L2.5 6.5L7.5 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {isCreating ? "Creating..." : "Create API Key"}
                </button>
              </div>

              {generatedKey && (
                <div
                  className="mt-4"
                  style={{
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <code className="flex-1 break-all" style={{ fontSize: 11, fontFamily: "'Geist Mono', monospace", color: "#d4cfc6" }}>
                      {generatedKey}
                    </code>
                    <button
                      onClick={() => void copyToClipboard(generatedKey)}
                      className="shrink-0"
                      style={{
                        fontFamily: "'Geist Mono', monospace",
                        fontSize: 10,
                        fontWeight: 500,
                        color: copied ? "#3ecf7e" : "#888",
                        background: "#111",
                        border: "1px solid #1e1e1e",
                        borderRadius: 7,
                        padding: "6px 12px",
                        cursor: "pointer",
                      }}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <p style={{ fontSize: 10, color: "#4a4a4a", marginTop: 8, letterSpacing: "0.02em" }}>
                    Copy this key now — you won&apos;t be able to see it again.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* API Keys List */}
          <div
            style={{
              background: "#0e0e0e",
              border: "1px solid #1c1c1c",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <div
              className="flex items-start justify-between"
              style={{ padding: "18px 20px", borderBottom: "1px solid #1c1c1c", gap: 12 }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", marginBottom: 4 }}>
                  API Keys
                </div>
                <div style={{ fontSize: 10, color: "#4a4a4a", letterSpacing: "0.02em", lineHeight: 1.5 }}>
                  Your existing API keys for authenticating with the Hoops API
                </div>
              </div>
            </div>
            <div style={{ padding: "18px 20px" }}>
              {apiKeys.length === 0 ? (
                <div className="flex flex-col items-center justify-center" style={{ padding: "28px 0 20px", gap: 8 }}>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" style={{ opacity: 0.15 }}>
                    <rect x="4" y="8" width="24" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M4 13h24" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="8" y="18" width="8" height="2" rx="1" fill="currentColor" opacity="0.5"/>
                  </svg>
                  <span style={{ fontSize: 10.5, color: "#4a4a4a", letterSpacing: "0.02em" }}>
                    No API keys yet. Create one above to get started.
                  </span>
                </div>
              ) : (
                <table className="w-full" style={{ fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1c1c1c" }}>
                      <th className="text-left" style={{ padding: "8px 0", fontWeight: 500, color: "#4a4a4a", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>Name</th>
                      <th className="text-left" style={{ padding: "8px 0", fontWeight: 500, color: "#4a4a4a", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>Created</th>
                      <th className="text-left" style={{ padding: "8px 0", fontWeight: 500, color: "#4a4a4a", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>Last used</th>
                      <th className="text-right" style={{ padding: "8px 0", fontWeight: 500, color: "#4a4a4a", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((apiKey) => (
                      <tr key={apiKey.key} style={{ borderBottom: "1px solid #161616" }}>
                        <td style={{ padding: "12px 0", fontWeight: 500, color: "#c8c3ba" }}>{apiKey.name}</td>
                        <td style={{ padding: "12px 0", color: "#4a4a4a" }}>{new Date(apiKey.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: "12px 0", color: "#4a4a4a" }}>
                          {apiKey.lastUsed ? new Date(apiKey.lastUsed).toLocaleDateString() : "Never"}
                        </td>
                        <td className="text-right" style={{ padding: "12px 0" }}>
                          <button
                            disabled
                            style={{
                              background: "none",
                              border: "none",
                              color: "#3a3a3a",
                              cursor: "not-allowed",
                              padding: 4,
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M2 4h10M5 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4M11 4v7.5a1 1 0 01-1 1H4a1 1 0 01-1-1V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
